import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem("pc_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
api.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e.response?.status === 401 && !e.config.url.includes("/auth/") && !window.location.pathname.startsWith("/portal")) {
      localStorage.removeItem("pc_token");
      window.location.href = "/login";
    }
    return Promise.reject(e);
  }
);

export function fmtErr(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
}

const clean = (p) => Object.fromEntries(Object.entries(p || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined));

export function useApi(path, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const key = path ? path + JSON.stringify(clean(params)) : null;
  const load = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    try {
      const r = await api.get(path, { params: clean(params) });
      setData(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    load();
  }, [load]);
  return { data, loading, reload: load, setData };
}

export const peso = (n) =>
  n === null || n === undefined ? "—" : "₱" + Number(n).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const dt = (s, opts = {}) =>
  s
    ? new Date(s).toLocaleString("en-PH", { timeZone: "Asia/Manila", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false, ...opts })
    : "—";

export const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" });

export const dur = (m) => {
  if (m === null || m === undefined) return "—";
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60;
  return `${d ? d + "d " : ""}${h ? h + "h " : ""}${mm}m`;
};

export const label = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export async function download(path, params, filename) {
  try {
    const r = await api.get(path, { params: clean(params), responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filename}`);
  } catch (e) {
    toast.error(e?.response?.status === 403 ? "Export permission required" : "Export failed");
  }
}

export function fileToDataUrl(file, max = 640) {
  return new Promise((res) => {
    const img = new Image();
    const rd = new FileReader();
    rd.onload = () => {
      img.onload = () => {
        const s = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = img.width * s;
        c.height = img.height * s;
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        res(c.toDataURL("image/jpeg", 0.7));
      };
      img.src = rd.result;
    };
    rd.readAsDataURL(file);
  });
}

export const METHODS = [
  { value: "gcash", label: "GCash" },
  { value: "maya", label: "Maya" },
  { value: "qrph", label: "QR Ph" },
  { value: "credit_card", label: "Credit Card" },
  { value: "debit_card", label: "Debit Card" },
  { value: "payment_link", label: "Online Payment Link" },
  { value: "cash", label: "Cash (controlled exception)" },
];
